/**
 * Immersive (fullscreen) toggle for the redesigned game play pages.
 *
 * A `[data-immersive-enter]` control opens immersive mode: it adds
 * `body.immersive` (the play-page CSS turns this into a dark full-screen
 * backdrop with the board centered) and requests the browser Fullscreen API on
 * the document element as an enhancement. A `[data-immersive-exit]` control,
 * the Escape key, or the browser leaving fullscreen all close it again.
 *
 * Fullscreen is requested on `document.documentElement` (not `document.body`)
 * so it never collides with FullscreenManager's `body:fullscreen` styling.
 *
 * A singleton guard makes the module idempotent: Vite HMR (or a second import)
 * must not install duplicate listeners, which would fire the fullscreen request
 * twice in one gesture and make the browser bounce straight back out.
 */

declare global {
  interface Window {
    __ppImmersiveInstalled?: boolean;
  }
}

if (!window.__ppImmersiveInstalled) {
  window.__ppImmersiveInstalled = true;

  // Safari before 16.4 only has the webkit-prefixed Fullscreen API.
  const inNativeFullscreen = (): boolean =>
    !!document.fullscreenElement || ('webkitFullscreenElement' in document && !!document.webkitFullscreenElement);

  const isImmersive = (): boolean => document.body.classList.contains('immersive');

  function requestFullscreen(): void {
    const el = document.documentElement;
    try {
      if (typeof el.requestFullscreen === 'function') {
        el.requestFullscreen().catch(() => {});
      } else if ('webkitRequestFullscreen' in el && typeof el.webkitRequestFullscreen === 'function') {
        el.webkitRequestFullscreen();
      }
    } catch {
      /* fullscreen is a best-effort enhancement over the CSS overlay */
    }
  }

  function exitFullscreen(): void {
    if (!inNativeFullscreen()) return;
    try {
      if (typeof document.exitFullscreen === 'function') {
        document.exitFullscreen().catch(() => {});
      } else if ('webkitExitFullscreen' in document && typeof document.webkitExitFullscreen === 'function') {
        document.webkitExitFullscreen();
      }
    } catch {
      /* ignore */
    }
  }

  function enterImmersive(): void {
    if (isImmersive()) return;
    document.body.classList.add('immersive');
    requestFullscreen();
    window.dispatchEvent(new Event('resize'));
  }

  function leaveImmersive(): void {
    if (!isImmersive()) return;
    document.body.classList.remove('immersive');
    exitFullscreen();
    window.dispatchEvent(new Event('resize'));
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-immersive-enter]')) {
      event.preventDefault();
      enterImmersive();
    } else if (target.closest('[data-immersive-exit]')) {
      event.preventDefault();
      leaveImmersive();
    }
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && isImmersive()) leaveImmersive();
    },
    true
  );

  // If the browser leaves native fullscreen on its own, drop the overlay too.
  const onFullscreenChange = (): void => {
    if (!inNativeFullscreen() && isImmersive()) document.body.classList.remove('immersive');
  };
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
}

export {};
